import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { EmailRecipients, EmailRecipientsOverride } from '@/components/use-email-recipients'
import { queryKeys } from '@/api/queries'
import { invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { invoiceKeys } from '@/features/invoices/keys'
import { USE_MOCK_AP_INVOICES } from '../mock'
import type {
  ApInvoicePoLinkInput,
  ApInvoicePoLinkAllocationInput,
  ApInvoiceActionInput,
  ApInvoiceAdjustmentResolutionInput,
} from '../types'
import type { ApInvoicePoLink, ApInvoiceAction, ApInvoiceDetail } from '../view-models'

export function useUpdateInvoicePoLinkAllocation(uuid: string, linkId: number) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoicePoLinkAllocationInput) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoicePoLink>(token, `/dashboard/ap-invoices/${uuid}/po-links/${linkId}`, {
        method: 'PATCH',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useLinkInvoicePo(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoicePoLinkInput) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          id: 9882,
          link_source: 'manual',
          link_confidence: 'manual_review',
          ...body,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoicePoLink>(token, `/dashboard/ap-invoices/${uuid}/po-links`, {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
  })
}

export function useCreateInvoiceAction(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoiceActionInput) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          id: 9883,
          status: 'open' as const,
          created_at: new Date().toISOString(),
          ...body,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceAction>(token, `/dashboard/ap-invoices/${uuid}/actions`, {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
  })
}

export function useResolveInvoiceAction(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      status,
      assigned_role,
      assigned_user_id,
      due_date,
      resolution_type,
      resolution_data,
    }: {
      actionId: number
      status?: string
      assigned_role?: string
      assigned_user_id?: number | null
      due_date?: string | null
      resolution_type?: string | null
      resolution_data?: Record<string, unknown> | null
    }) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          id: actionId,
          action_type: 'manual_review',
          summary: 'Action updated',
          status: status ?? 'open',
          assigned_role,
          assigned_user_id,
          due_date,
          resolution_type,
          resolution_data,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceAction>(
        token,
        `/dashboard/ap-invoices/${uuid}/actions/${actionId}`,
        {
          method: 'PATCH',
          json: {
            status,
            assigned_role,
            assigned_user_id,
            due_date,
            resolution_type,
            resolution_data,
          },
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
  })
}

export function useResolveInvoiceAdjustment(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      body,
    }: {
      actionId: number
      body: ApInvoiceAdjustmentResolutionInput
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(
        token,
        `/dashboard/ap-invoices/${uuid}/actions/${actionId}/resolve-adjustment`,
        { method: 'POST', json: body },
      )
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(uuid), invoice)
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(uuid) })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.approvalCapabilities(uuid),
      })
    },
  })
}

export function useDismissInvoiceAdjustment(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, reason }: { actionId: number; reason: string }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(
        token,
        `/dashboard/ap-invoices/${uuid}/actions/${actionId}/dismiss-adjustment`,
        { method: 'POST', json: { reason } },
      )
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(uuid), invoice)
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(uuid) })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.approvalCapabilities(uuid),
      })
    },
  })
}

export function useDecideManufacturerTerms(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      decision,
    }: {
      actionId: number
      decision: 'update' | 'keep'
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(
        token,
        `/dashboard/ap-invoices/${uuid}/actions/${actionId}/manufacturer-terms`,
        { method: 'POST', json: { decision } },
      )
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(uuid), invoice)
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all })
    },
  })
}

/** Send the reply staff picked (and edited) on the invoice's email thread and close the item (USA-1418). */
export function useSendInvoiceActionReply(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      bodyText,
      resolutionType,
      recipients,
    }: {
      actionId: number
      bodyText: string
      resolutionType: string
      /** Who staff chose to send it to (USA-1663); omitted keeps the conversation's contacts. */
      recipients?: EmailRecipientsOverride
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceAction>(
        token,
        `/dashboard/ap-invoices/${uuid}/actions/${actionId}/send-reply`,
        { method: 'POST', json: { body_text: bodyText, resolution_type: resolutionType, ...recipients } },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}

/** Who an invoice item's reply goes to unless staff change it (USA-1663). */
export function useInvoiceActionReplyRecipients(uuid: string, actionId: number, enabled: boolean) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: [...invoiceKeys.all, 'action-reply-recipients', uuid, actionId],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<EmailRecipients> => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      const result = await apiFetch<{ to_emails?: string[]; cc_emails?: string[] }>(
        token,
        `/dashboard/ap-invoices/${uuid}/actions/${actionId}/reply-recipients`,
      )
      return { to: result.to_emails ?? [], cc: result.cc_emails ?? [] }
    },
  })
}
